import { useState, useEffect } from 'react';
import { useWorkspace } from '../hooks/useWorkspace';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../lib/api';
import { REGIONS } from '@seedgovernance/shared';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Card, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';

type SettingsTab = 'profile' | 'workspace' | 'billing' | 'notifications';

const TABS: { key: SettingsTab; label: string }[] = [
  { key: 'profile', label: 'Profile' },
  { key: 'workspace', label: 'Workspace' },
  { key: 'billing', label: 'Billing' },
  { key: 'notifications', label: 'Notifications' },
];

interface BillingInfo {
  plan: string;
  planLabel: string;
  price: string;
  interval: string;
  frameworksUsed: number;
  frameworksLimit: number;
  membersUsed: number;
  membersLimit: number;
  storageUsedGb: number;
  storageLimitGb: number;
}

interface NotificationPrefs {
  digestFrequency: 'daily' | 'weekly' | 'none';
  complianceUpdates: boolean;
  teamChanges: boolean;
  billingAlerts: boolean;
}

export function Settings() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  if (!currentWorkspace || !user) return null;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-white">Settings</h1>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-border">
        <nav className="flex gap-0 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-brand text-brand-light'
                  : 'border-transparent text-slate-400 hover:text-white hover:border-border'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'profile' && <ProfileTab />}
      {activeTab === 'workspace' && <WorkspaceTab />}
      {activeTab === 'billing' && <BillingTab />}
      {activeTab === 'notifications' && <NotificationsTab />}
    </div>
  );
}

function ProfileTab() {
  const { user } = useAuth();

  if (!user) return null;

  const initials = user.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile Information</CardTitle>
      </CardHeader>

      <div className="flex items-start gap-6">
        {/* Avatar */}
        <div className="w-16 h-16 rounded-full bg-brand/20 flex items-center justify-center shrink-0">
          <span className="text-xl font-display font-bold text-brand-light">
            {initials}
          </span>
        </div>

        {/* Info */}
        <div className="space-y-4 flex-1">
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-slate-300">
              Name
            </label>
            <p className="px-3 py-2 bg-surface-dark border border-border rounded-lg text-white text-sm">
              {user.name}
            </p>
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-slate-300">
              Email
            </label>
            <p className="px-3 py-2 bg-surface-dark border border-border rounded-lg text-white text-sm">
              {user.email}
            </p>
          </div>

          <p className="text-xs text-slate-500">
            Profile information is managed through your identity provider.
          </p>
        </div>
      </div>
    </Card>
  );
}

function WorkspaceTab() {
  const { currentWorkspace } = useWorkspace();
  const [companyName, setCompanyName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    if (!currentWorkspace) return;
    setCompanyName(currentWorkspace.companyName || '');
    setWorkspaceName(currentWorkspace.workspaceName || '');
  }, [currentWorkspace]);

  if (!currentWorkspace) return null;

  const region = REGIONS[currentWorkspace.region as keyof typeof REGIONS];

  async function handleSave() {
    if (!currentWorkspace) return;
    setSaving(true);
    setError(null);
    setSuccess(false);

    const { error: apiError } = await apiClient.patch(
      `/api/tenants/${currentWorkspace.tenantId}`,
      { companyName: companyName.trim(), workspaceName: workspaceName.trim() }
    );

    if (apiError) {
      setError(apiError.message);
    } else {
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    }
    setSaving(false);
  }

  async function handleDelete() {
    if (!currentWorkspace) return;
    setDeleting(true);

    const { error: apiError } = await apiClient.delete(
      `/api/tenants/${currentWorkspace.tenantId}`
    );

    if (apiError) {
      setError(apiError.message);
      setDeleting(false);
    } else {
      window.location.href = '/login';
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Workspace Details</CardTitle>
        </CardHeader>

        <div className="space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-growth/10 border border-growth/20 rounded-lg p-3">
              <p className="text-sm text-growth">Changes saved successfully.</p>
            </div>
          )}

          <Input
            label="Company Name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />

          <Input
            label="Workspace Name"
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
          />

          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-slate-300">
              Region
            </label>
            <p className="px-3 py-2 bg-surface-dark border border-border rounded-lg text-white text-sm">
              {region?.label || currentWorkspace.region}
            </p>
            <p className="text-xs text-slate-500">
              Region cannot be changed after workspace creation.
            </p>
          </div>

          <div className="pt-2">
            <Button loading={saving} onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        </div>
      </Card>

      {/* Danger zone */}
      <Card className="border-red-500/20">
        <CardHeader>
          <CardTitle className="text-red-400">Danger Zone</CardTitle>
        </CardHeader>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-white font-medium">Delete Workspace</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Permanently delete this workspace and all its data. This action cannot be undone.
            </p>
          </div>
          <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
            Delete Workspace
          </Button>
        </div>
      </Card>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeleteConfirmText('');
        }}
        title="Delete Workspace"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteConfirmText('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleting}
              disabled={deleteConfirmText !== currentWorkspace.companyName}
              onClick={handleDelete}
            >
              Delete Permanently
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-300">
            This will permanently delete the workspace{' '}
            <span className="font-semibold text-white">
              {currentWorkspace.companyName}
            </span>{' '}
            and all associated data including frameworks, deliverables, files, and team members.
          </p>
          <Input
            label={`Type "${currentWorkspace.companyName}" to confirm`}
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={currentWorkspace.companyName}
          />
        </div>
      </Modal>
    </div>
  );
}

function BillingTab() {
  const { currentWorkspace } = useWorkspace();
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!currentWorkspace) return;
    fetchBilling();
  }, [currentWorkspace]);

  async function fetchBilling() {
    if (!currentWorkspace) return;
    setLoading(true);

    const { data, error: apiError } = await apiClient.get<BillingInfo>(
      `/api/tenants/${currentWorkspace.tenantId}/billing`
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      setBilling(data);
    }
    setLoading(false);
  }

  async function handleManageBilling() {
    if (!currentWorkspace) return;
    setRedirecting(true);

    const { data, error: apiError } = await apiClient.post<{ url: string }>(
      `/api/tenants/${currentWorkspace.tenantId}/billing/portal`
    );

    if (apiError) {
      setError(apiError.message);
      setRedirecting(false);
    } else if (data?.url) {
      window.location.href = data.url;
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="w-6 h-6 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Current plan */}
      <Card>
        <CardHeader>
          <CardTitle>Current Plan</CardTitle>
        </CardHeader>

        {billing ? (
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-lg font-display font-bold text-white">
                  {billing.planLabel}
                </p>
                <Badge variant="brand">{billing.plan}</Badge>
              </div>
              <p className="text-sm text-slate-400 mt-0.5">
                {billing.price} / {billing.interval}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="secondary" onClick={() => {}}>
                Upgrade Plan
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-400">
            Billing information is not available.
          </p>
        )}
      </Card>

      {/* Usage */}
      {billing && (
        <Card>
          <CardHeader>
            <CardTitle>Usage</CardTitle>
          </CardHeader>

          <div className="space-y-5">
            <UsageBar
              label="Frameworks"
              used={billing.frameworksUsed}
              limit={billing.frameworksLimit}
              unit=""
            />
            <UsageBar
              label="Team Members"
              used={billing.membersUsed}
              limit={billing.membersLimit}
              unit=""
            />
            <UsageBar
              label="Storage"
              used={billing.storageUsedGb}
              limit={billing.storageLimitGb}
              unit="GB"
            />
          </div>
        </Card>
      )}

      {/* Billing management */}
      <Card>
        <CardHeader>
          <CardTitle>Billing Management</CardTitle>
        </CardHeader>

        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            loading={redirecting}
            onClick={handleManageBilling}
          >
            Manage Billing
          </Button>
          <Button variant="ghost" onClick={handleManageBilling}>
            View Invoices
          </Button>
        </div>
      </Card>
    </div>
  );
}

function UsageBar({
  label,
  used,
  limit,
  unit,
}: {
  label: string;
  used: number;
  limit: number;
  unit: string;
}) {
  const percent = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const isHigh = percent >= 80;

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm text-slate-300">{label}</span>
        <span className="text-sm text-slate-400">
          {used}
          {unit} / {limit}
          {unit}
        </span>
      </div>
      <div className="h-1.5 bg-surface-dark rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            isHigh ? 'bg-yellow-500' : 'bg-brand'
          }`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function NotificationsTab() {
  const { currentWorkspace } = useWorkspace();
  const [prefs, setPrefs] = useState<NotificationPrefs>({
    digestFrequency: 'weekly',
    complianceUpdates: true,
    teamChanges: true,
    billingAlerts: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!currentWorkspace) return;
    fetchPrefs();
  }, [currentWorkspace]);

  async function fetchPrefs() {
    if (!currentWorkspace) return;
    setLoading(true);

    const { data, error: apiError } = await apiClient.get<NotificationPrefs>(
      `/api/tenants/${currentWorkspace.tenantId}/notification-preferences`
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      setPrefs(data);
    }
    setLoading(false);
  }

  async function handleSave() {
    if (!currentWorkspace) return;
    setSaving(true);
    setError(null);
    setSuccess(false);

    const { error: apiError } = await apiClient.patch(
      `/api/tenants/${currentWorkspace.tenantId}/notification-preferences`,
      prefs
    );

    if (apiError) {
      setError(apiError.message);
    } else {
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    }
    setSaving(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="w-6 h-6 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-growth/10 border border-growth/20 rounded-lg p-3">
          <p className="text-sm text-growth">Preferences saved successfully.</p>
        </div>
      )}

      {/* Digest frequency */}
      <Card>
        <CardHeader>
          <CardTitle>Email Digest</CardTitle>
        </CardHeader>

        <Select
          label="Digest Frequency"
          options={[
            { value: 'daily', label: 'Daily' },
            { value: 'weekly', label: 'Weekly' },
            { value: 'none', label: 'None' },
          ]}
          value={prefs.digestFrequency}
          onChange={(e) =>
            setPrefs((prev) => ({
              ...prev,
              digestFrequency: e.target.value as NotificationPrefs['digestFrequency'],
            }))
          }
        />
      </Card>

      {/* Event toggles */}
      <Card>
        <CardHeader>
          <CardTitle>Event Notifications</CardTitle>
        </CardHeader>

        <div className="space-y-4">
          <NotificationToggle
            label="Compliance Updates"
            description="Notifications when deliverables are updated or statuses change"
            checked={prefs.complianceUpdates}
            onChange={(checked) =>
              setPrefs((prev) => ({ ...prev, complianceUpdates: checked }))
            }
          />
          <NotificationToggle
            label="Team Changes"
            description="Notifications when team members are added or removed"
            checked={prefs.teamChanges}
            onChange={(checked) =>
              setPrefs((prev) => ({ ...prev, teamChanges: checked }))
            }
          />
          <NotificationToggle
            label="Billing Alerts"
            description="Notifications for billing events, plan changes, and payment issues"
            checked={prefs.billingAlerts}
            onChange={(checked) =>
              setPrefs((prev) => ({ ...prev, billingAlerts: checked }))
            }
          />
        </div>
      </Card>

      <div>
        <Button loading={saving} onClick={handleSave}>
          Save Preferences
        </Button>
      </div>
    </div>
  );
}

function NotificationToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 rounded border-border bg-surface-dark text-brand focus:ring-brand/50"
      />
      <div>
        <p className="text-sm font-medium text-white">{label}</p>
        <p className="text-xs text-slate-500 mt-0.5">{description}</p>
      </div>
    </label>
  );
}
